import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, FileText, Calendar, Hash } from "lucide-react";

const VERSION = "1.0";
const FECHA_VIGENCIA = "1 de mayo de 2026";
const HASH = "sha256:7a3f9c2b8e5d1f4a6b9c0d2e8f1a5b7c";

export default function TerminosPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="terminos-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-6" data-testid="terminos-back-link">
            <ChevronLeft size={16} /> Volver al inicio
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-yellow-400 flex items-center justify-center text-blue-900">
              <FileText size={22} />
            </div>
            <span className="text-xs tracking-[3px] text-blue-200 uppercase">Documento Legal</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight">
            Términos y Condiciones <span className="text-yellow-400">de Uso</span>
          </h1>
          <p className="mt-3 text-blue-100 max-w-2xl">
            Sistema Integral de Recaudación Tributaria Municipal — SIRET. Este documento regula el acceso y uso de la plataforma por parte de funcionarios, contribuyentes y terceros autorizados.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-blue-200">
            <div className="flex items-center gap-1.5"><Hash size={13} /> Versión {VERSION}</div>
            <div className="flex items-center gap-1.5"><Calendar size={13} /> Vigente desde: {FECHA_VIGENCIA}</div>
            <div className="flex items-center gap-1.5"><FileText size={13} /> Hash documento: <code className="bg-white/10 px-1.5 py-0.5 rounded">{HASH}</code></div>
          </div>
        </div>
      </header>

      {/* Cuerpo */}
      <main className="max-w-4xl mx-auto px-6 py-12 prose prose-slate prose-headings:text-blue-900 prose-headings:font-bold max-w-none">

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">1. Aceptación de los Términos</h2>
          <p className="text-slate-700 leading-relaxed">
            El acceso y uso del Sistema SIRET implica la aceptación plena y sin reservas de los presentes Términos y Condiciones de Uso. El usuario que no acepte expresamente estos términos no podrá utilizar la plataforma. La aceptación se manifiesta mediante el clic en el casillero correspondiente durante el registro o por el simple uso del sistema una vez autenticado.
          </p>
          <p className="text-slate-700 leading-relaxed mt-2">
            Cada aceptación queda registrada en el audit log forense del sistema con timestamp UTC, dirección IP del usuario, versión del documento aceptado y hash criptográfico SHA-256 del contenido vigente al momento de la aceptación, constituyendo evidencia probatoria válida.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">2. Definiciones</h2>
          <ul className="space-y-2 text-slate-700">
            <li><strong>SIRET:</strong> Sistema Integral de Recaudación Tributaria Municipal, plataforma SaaS para la administración de tributos municipales.</li>
            <li><strong>Plataforma:</strong> Conjunto de aplicaciones, APIs, bases de datos y servicios que conforman el sistema SIRET.</li>
            <li><strong>Usuario:</strong> Cualquier persona natural o jurídica que accede a la plataforma, incluyendo funcionarios, contribuyentes y terceros autorizados.</li>
            <li><strong>Contribuyente:</strong> Sujeto pasivo de obligaciones tributarias municipales registrado en la plataforma.</li>
            <li><strong>Operador del Sistema:</strong> Entidad técnica responsable del desarrollo, operación y mantenimiento de SIRET.</li>
            <li><strong>Municipio Cliente:</strong> Entidad pública municipal contratante del servicio para la gestión de su recaudación tributaria.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">3. Uso Aceptable</h2>
          <p className="text-slate-700 leading-relaxed">El usuario se compromete a utilizar SIRET exclusivamente para los fines legítimos previstos en su condición de funcionario o contribuyente. Queda expresamente prohibido:</p>
          <ul className="list-disc pl-6 mt-3 space-y-1 text-slate-700">
            <li>Acceder o intentar acceder a información, módulos o funcionalidades para los cuales no posee autorización expresa.</li>
            <li>Realizar ingeniería inversa, descompilación, análisis de vulnerabilidades sin autorización formal o cualquier intento de comprometer la seguridad de la plataforma.</li>
            <li>Utilizar herramientas automatizadas (bots, scrapers, crawlers) sin autorización contractual previa.</li>
            <li>Suplantar la identidad de otro usuario, compartir credenciales o permitir el uso de su cuenta a terceros.</li>
            <li>Cargar, transmitir o difundir información falsa, fraudulenta, ofensiva, ilícita o que viole derechos de terceros.</li>
            <li>Interferir con el normal funcionamiento del sistema mediante ataques de denegación de servicio, inyección de código u otras técnicas hostiles.</li>
            <li>Utilizar la plataforma para fines distintos a la gestión tributaria municipal autorizada.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">4. Responsabilidades del Usuario</h2>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Custodiar con diligencia razonable sus credenciales de acceso, incluyendo contraseñas, códigos MFA y dispositivos de autenticación.</li>
            <li>Notificar de inmediato al Operador del Sistema cualquier sospecha de uso no autorizado de su cuenta.</li>
            <li>Mantener actualizada y veraz la información personal y tributaria registrada.</li>
            <li>Cumplir con las obligaciones tributarias municipales conforme a la normativa aplicable, siendo SIRET una herramienta de gestión y no un sustituto de las responsabilidades fiscales del contribuyente.</li>
            <li>Conservar los comprobantes y constancias generados por el sistema durante los plazos exigidos por la legislación venezolana (mínimo 10 años para documentos tributarios).</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">5. Responsabilidades del Operador del Sistema</h2>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Mantener la disponibilidad del sistema conforme a los SLAs comprometidos en el contrato con el Municipio Cliente.</li>
            <li>Aplicar medidas técnicas y organizativas adecuadas para garantizar la seguridad e integridad de los datos (cifrado TLS 1.3, hash bcrypt, audit log forense SHA-256, MFA, honeytokens).</li>
            <li>Realizar respaldos automatizados periódicos según la política formal de backups (estrategia 3-2-1-1).</li>
            <li>Informar al Municipio Cliente sobre incidentes de seguridad relevantes dentro de los plazos establecidos en el contrato.</li>
            <li>Mantener canales de soporte técnico activos durante los horarios comprometidos.</li>
            <li>Cumplir con la normativa venezolana de protección de datos (Ley 37.252) y adoptar mejores prácticas internacionales (GDPR).</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">6. Limitación de Responsabilidad</h2>
          <p className="text-slate-700 leading-relaxed">
            SIRET se proporciona "tal cual" en función del estado vigente de la plataforma al momento del uso. Aunque el Operador del Sistema aplica las mejores prácticas técnicas y de seguridad disponibles en la industria, no garantiza disponibilidad ininterrumpida del 100% ni la inexistencia absoluta de errores. La responsabilidad del Operador queda limitada conforme a las cláusulas establecidas en el contrato comercial vigente con el Municipio Cliente.
          </p>
          <p className="text-slate-700 leading-relaxed mt-2">
            En ningún caso el Operador será responsable por daños indirectos, consecuentes o lucro cesante derivados del uso o imposibilidad de uso del sistema, salvo cuando dichos daños deriven de dolo o culpa grave debidamente probados.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">7. Propiedad Intelectual</h2>
          <p className="text-slate-700 leading-relaxed">
            Todos los derechos de propiedad intelectual sobre el sistema SIRET, incluyendo el código fuente, los diseños de interfaz, las marcas, los logotipos, la documentación técnica y los algoritmos de cálculo tributario, son propiedad del Operador del Sistema o de sus licenciantes, según corresponda. Los datos cargados por los Municipios Cliente y por los contribuyentes permanecen como propiedad de sus respectivos titulares, otorgándose al Operador únicamente las licencias necesarias para la prestación del servicio.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">8. Modificación de los Términos</h2>
          <p className="text-slate-700 leading-relaxed">
            El Operador del Sistema se reserva el derecho a modificar los presentes Términos y Condiciones cuando lo estime necesario por motivos legales, regulatorios, técnicos o de mejora del servicio. Los cambios se notificarán con al menos 30 días de antelación a través de los canales habituales del sistema. Al ingresar nuevamente, el usuario deberá aceptar expresamente la versión vigente, quedando bloqueado el acceso hasta su aceptación.
          </p>
          <p className="text-slate-700 leading-relaxed mt-2">
            Cada versión histórica de los términos se conserva con su hash criptográfico SHA-256 inmutable, permitiendo verificar exactamente cuál versión aceptó el usuario en cada momento histórico.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">9. Suspensión y Terminación</h2>
          <p className="text-slate-700 leading-relaxed">
            El Operador del Sistema podrá suspender o cancelar el acceso del usuario en caso de incumplimiento grave de los presentes términos, sospecha fundada de actividad fraudulenta, requerimiento de autoridad competente o por finalización de la relación contractual con el Municipio Cliente. La suspensión no exonera al usuario del cumplimiento de sus obligaciones tributarias preexistentes.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">10. Jurisdicción y Legislación Aplicable</h2>
          <p className="text-slate-700 leading-relaxed">
            Los presentes Términos y Condiciones se rigen por las leyes de la República Bolivariana de Venezuela. Cualquier controversia derivada del uso de la plataforma será sometida a la jurisdicción de los tribunales competentes en la circunscripción judicial donde tenga su sede principal el Municipio Cliente, salvo pacto contractual en contrario.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-blue-900 mb-3">11. Contacto</h2>
          <p className="text-slate-700 leading-relaxed">
            Para consultas, reclamos o ejercicio de derechos relacionados con estos términos, el usuario puede comunicarse al canal oficial dispuesto por el Operador del Sistema:
          </p>
          <div className="mt-3 bg-slate-100 rounded-lg p-4 text-sm">
            <div><strong>Correo legal:</strong> legal@siret360.com</div>
            <div><strong>Soporte técnico:</strong> soporte@siret360.com</div>
            <div><strong>DPO (Protección de Datos):</strong> dpo@siret360.com</div>
            <div><strong>Portal de tickets:</strong> a través del Sistema SIRET</div>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 text-center">
          <p>Documento publicado oficialmente · SIRET — Sistema Integral de Recaudación Tributaria Municipal</p>
          <p className="mt-1">Versión {VERSION} · Vigente desde {FECHA_VIGENCIA} · Hash de integridad: <code>{HASH}</code></p>
          <p className="mt-3">
            Ver también: <Link to="/privacidad" className="text-blue-700 underline" data-testid="terminos-link-privacidad">Política de Privacidad</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
